import { useEffect, useRef, useState, FormEvent } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { AppShell } from '../components/AppShell';
import { SignaturePad, type SignaturePadHandle } from '../components/SignaturePad';
import { DictateButton } from '../components/DictateButton';
import { PhotoAttach, type LocalPhoto } from '../components/PhotoAttach';
import { useUserOrg } from '../lib/org';
import { getSite, createForm, setFormSignaturePath, type Site } from '../lib/db';
import { uploadSignature } from '../lib/storage';
import { supabase } from '../lib/supabase';
import { enqueue as enqueueOutbox, blobToDataUrl } from '../lib/outbox';
import { useDraftAutosave, loadDraft, clearDraft, formatDraftAge } from '../lib/draftAutosave';
import { LoadingIndicator } from '../components/LoadingIndicator';
import { RIDDOR_ITEMS } from '../lib/riddor';

/** Pure helper — true if we currently appear to be online. iOS Safari can
 *  report `onLine === true` while requests still fail, so callers should
 *  treat this as a hint, not a guarantee. */
function isOnline(): boolean {
  return typeof navigator === 'undefined' ? true : navigator.onLine;
}



const TYPES = ['', 'Accident', 'Near-miss', 'Dangerous occurrence', 'Property damage', 'Ill-health'];
const TREATMENTS = ['No', 'A&E', 'GP', 'Minor injuries unit'];
const TIME_OFF = [
  'No time off',
  'Returned to work same day',
  'Off work — 1 to 3 days',
  'Off work — 4 to 7 days',
  'Off work — more than 7 days',
  'Off work — not known yet',
];

export function FormAccident() {
  const { siteId } = useParams<{ siteId: string }>();
  const nav = useNavigate();
  const { org } = useUserOrg();
  const formIdRef = useRef<string>(crypto.randomUUID());
  const sigRef = useRef<SignaturePadHandle>(null);

  const [site, setSite] = useState<Site | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [submitting, setSubmitting] = useState(false);

  const now = new Date();
  const [type, setType] = useState('');
  const [date, setDate] = useState(now.toISOString().slice(0, 10));
  const [time, setTime] = useState(now.toTimeString().slice(0, 5));
  const [location, setLocation] = useState('');
  const [person, setPerson] = useState('');
  const [employer, setEmployer] = useState('');
  // Accident book (BI 510) asks for the injured person's address + job.
  const [personAddress, setPersonAddress] = useState('');
  const [personOccupation, setPersonOccupation] = useState('');
  const [bodyPart, setBodyPart] = useState('');
  const [hospital, setHospital] = useState('');
  const [timeOff, setTimeOff] = useState(TIME_OFF[0]);
  const [returnDate, setReturnDate] = useState('');
  const [actionOwner, setActionOwner] = useState('');
  const [actionDue, setActionDue] = useState('');
  const [what, setWhat] = useState('');
  const [injury, setInjury] = useState('');
  const [fa, setFa] = useState('');
  const [treatment, setTreatment] = useState('No');
  // witnesses is now a structured list: [{ name, statement }]
  const [witnessList, setWitnessList] = useState<Array<{ name: string; statement: string }>>([]);
  const [riddorRef, setRiddorRef] = useState('');
  const [riddorSubmittedAt, setRiddorSubmittedAt] = useState('');
  const [cause, setCause] = useState('');
  const [actionTaken, setActionTaken] = useState('');
  const [byName, setByName] = useState('');
  const [byRole, setByRole] = useState('');

  const [riddor, setRiddor] = useState<Record<string, boolean>>({});

  // Completion state — when set, the page swaps from the form to a
  // green success card showing reference, time, site, and "View" / "Done"
  // CTAs. Matches the Pack-everything success card pattern, and gives the
  // user a clear "yes it saved" moment for what is by definition a
  // stressful entry.
  const [done, setDone] = useState<null | {
    when: Date;
    siteName: string;
    formId: string;
    type: string;
    offline: boolean;
  }>(null);
  // Triage gate (UX review #22): 13 RIDDOR checkboxes was too much for a
  // user whose hands are shaking after an incident. Ask one question first
  // ("was anyone seriously hurt?"). Only reveal the 13 categories if Yes
  // or Not sure. If No, skip the whole RIDDOR section.
  const [riddorTriage, setRiddorTriage] = useState<'unset' | 'yes' | 'unsure' | 'no'>('unset');
  const [photos, setPhotos] = useState<LocalPhoto[]>([]);

  // Autosave (UX review #23): incidents happen at the worst possible
  // moments and the user often gets a phone call mid-fill. iOS Safari
  // is happy to kill the tab while it's backgrounded. This persists all
  // serialisable form state to localStorage every 5s (debounced) so a
  // 're-enter the page' flow can offer to restore. Signatures + photos
  // are NOT saved (binary + canvas state).
  const draftKey = `form.accident:${siteId ?? 'unknown'}:${formIdRef.current}`;
  const [restorable, setRestorable] = useState<{ savedAt: number } | null>(null);
  const [restoreBannerDismissed, setRestoreBannerDismissed] = useState(false);

  // On mount, check for a saved draft. We can't restore yet (don't know
  // the user's intent) so we just remember it's there and show the banner.
  useEffect(() => {
    const d = loadDraft<Record<string, unknown>>(draftKey);
    if (d) setRestorable({ savedAt: d.savedAt });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Bundle the serialisable form state into one object the hook can persist.
  const draftPayload = {
    type, date, time, location, person, employer, what, injury, fa,
    treatment, witnessList, riddorRef, riddorSubmittedAt, cause,
    actionTaken, byName, byRole, riddor, riddorTriage,
    personAddress, personOccupation, bodyPart, hospital, timeOff, returnDate,
    actionOwner, actionDue,
  };
  useDraftAutosave(draftKey, draftPayload, { enabled: !submitting });

  function restoreDraft() {
    const d = loadDraft<typeof draftPayload>(draftKey);
    if (!d) { setRestorable(null); return; }
    const p = d.payload;
    setType(p.type ?? ''); setDate(p.date ?? date); setTime(p.time ?? time);
    setLocation(p.location ?? ''); setPerson(p.person ?? '');
    setEmployer(p.employer ?? ''); setWhat(p.what ?? '');
    setInjury(p.injury ?? ''); setFa(p.fa ?? ''); setTreatment(p.treatment ?? 'No');
    setWitnessList(p.witnessList ?? []); setRiddorRef(p.riddorRef ?? '');
    setRiddorSubmittedAt(p.riddorSubmittedAt ?? ''); setCause(p.cause ?? '');
    setActionTaken(p.actionTaken ?? ''); setByName(p.byName || byName);
    setByRole(p.byRole ?? ''); setRiddor(p.riddor ?? {});
    setRiddorTriage(p.riddorTriage ?? 'unset');
    setPersonAddress(p.personAddress ?? ''); setPersonOccupation(p.personOccupation ?? '');
    setBodyPart(p.bodyPart ?? ''); setHospital(p.hospital ?? '');
    setTimeOff(p.timeOff ?? TIME_OFF[0]); setReturnDate(p.returnDate ?? '');
    setActionOwner(p.actionOwner ?? ''); setActionDue(p.actionDue ?? '');
    setRestorable(null);
  }

  function discardDraft() {
    clearDraft(draftKey);
    setRestorable(null);
    setRestoreBannerDismissed(true);
  }

  useEffect(() => {
    if (!siteId) return;
    (async () => {
      try {
        const s = await getSite(siteId);
        setSite(s);
      } catch (e) {
        setError(e instanceof Error ? e.message : String(e));
      } finally {
        setLoading(false);
      }
    })();
    supabase.auth.getUser().then(({ data }) => {
      const name = (data.user?.user_metadata?.full_name as string | undefined) ?? '';
      if (name && !byName) setByName(name);
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [siteId]);

  // Triage 'no' always means not RIDDOR. Triage 'yes' or 'unsure' becomes
  // RIDDOR only if the user could actually identify a category — otherwise
  // the form saves with isRiddor=false but the duty-holder still gets the
  // "we couldn't classify this — call HSE if in any doubt" nudge below.
  const isRiddor = riddorTriage !== 'no' && Object.values(riddor).some(Boolean);
  const showRiddorCategories = riddorTriage === 'yes' || riddorTriage === 'unsure';

  /** Everything saved into forms.data for this report. */
  function buildData() {
    return {
      type, date, time, location, person, employer,
      person_address: personAddress.trim(),
      person_occupation: personOccupation.trim(),
      what, injury, body_part: bodyPart.trim(), fa, treatment,
      hospital: treatment !== 'No' ? hospital.trim() : '',
      time_off: timeOff,
      return_date: returnDate || null,
      witnesses: witnessList.filter((w) => w.name.trim() || w.statement.trim()),
      cause_imm: cause, cause_root: '',
      action: actionTaken,
      action_owner: actionOwner.trim(),
      action_due: actionDue || null,
      by_role: byRole,
      riddor_ref: riddorRef.trim() || null,
      riddor_submitted_at: riddorSubmittedAt || null,
    };
  }

  async function onSubmit(e: FormEvent) {
    e.preventDefault();
    if (!org || !site) return;
    setError(null);

    if (!type || !what.trim() || !actionTaken.trim() || !byName.trim()) {
      setError('Type, What happened, Action and Reported by are required.');
      return;
    }
    if (sigRef.current?.isEmpty()) {
      setError('Please sign the report.');
      return;
    }

    setSubmitting(true);
    try {
      const formId = formIdRef.current;
      const sigBlob = await sigRef.current!.toBlob();

      const formDataCore = buildData();

      // Offline path — convert signature + photos to base64 data URLs and
      // queue the whole thing in IndexedDB. On reconnect the outbox dispatcher
      // uploads everything to Storage and inserts the form row.
      if (!isOnline()) {
        const sigDataUrl = await blobToDataUrl(sigBlob);
        const photoDataUrls: string[] = [];
        for (const p of photos) photoDataUrls.push(await blobToDataUrl(p.blob));
        await enqueueOutbox('form.accident', org.org_id, {
          formId,
          orgId: org.org_id,
          siteId: site.id,
          kind: 'accident',
          title: `${type} — ${what.slice(0, 60)}`,
          performed_by_name: byName.trim(),
          data: formDataCore,
          is_riddor: isRiddor,
          riddor_flags: riddor,
          signature_data_url: sigDataUrl,
          photo_data_urls: photoDataUrls,
          photo_bucket: 'accident-photos',
        });
        clearDraft(draftKey);
        nav(`/sites/${site.id}/forms`);
        return;
      }

      // Online path — same as before
      const photoPaths: string[] = [];
      for (let i = 0; i < photos.length; i++) {
        const path = `${org.org_id}/form-${formId}/${i}.jpg`;
        const { error: upErr } = await supabase.storage
          .from('accident-photos')
          .upload(path, photos[i].blob, { contentType: 'image/jpeg', upsert: true });
        if (upErr) throw upErr;
        photoPaths.push(path);
      }
      await createForm({
        id: formId,
        orgId: org.org_id,
        siteId: site.id,
        kind: 'accident',
        title: `${type} — ${what.slice(0, 60)}`,
        performed_by_name: byName.trim(),
        data: { ...formDataCore, photo_paths: photoPaths },
        is_riddor: isRiddor,
        riddor_flags: riddor,
      });

      const sigPath = await uploadSignature({
        orgId: org.org_id,
        signInId: `form-${formId}`,
        side: 'in',
        blob: sigBlob,
      });
      await setFormSignaturePath(formId, sigPath);

      clearDraft(draftKey);
      setDone({
        when: new Date(),
        siteName: site.name,
        formId: formIdRef.current,
        type: type || 'Incident',
        offline: false,
      });
      setSubmitting(false);
      return;
    } catch (e) {
      // If we lost signal mid-save, fall back to the outbox so the user
      // doesn't have to refill the form.
      if (!isOnline()) {
        try {
          const formId = formIdRef.current;
          const sigBlob = await sigRef.current!.toBlob();
          const sigDataUrl = await blobToDataUrl(sigBlob);
          const photoDataUrls: string[] = [];
          for (const p of photos) photoDataUrls.push(await blobToDataUrl(p.blob));
          await enqueueOutbox('form.accident', org.org_id, {
            formId,
            orgId: org.org_id,
            siteId: site.id,
            kind: 'accident',
            title: `${type} — ${what.slice(0, 60)}`,
            performed_by_name: byName.trim(),
            data: buildData(),
            is_riddor: isRiddor,
            riddor_flags: riddor,
            signature_data_url: sigDataUrl,
            photo_data_urls: photoDataUrls,
            photo_bucket: 'accident-photos',
          });
          clearDraft(draftKey);
          setDone({
            when: new Date(),
            siteName: site.name,
            formId: formIdRef.current,
            type: type || 'Incident',
            offline: true,
          });
          setSubmitting(false);
          return;
        } catch (qErr) {
          setError(qErr instanceof Error ? qErr.message : String(qErr));
          setSubmitting(false);
          return;
        }
      }
      setError(e instanceof Error ? e.message : String(e));
      setSubmitting(false);
    }
  }

  if (loading) return <AppShell title="Accident report" back={`/sites/${siteId ?? ''}/forms`}><LoadingIndicator /></AppShell>;
  if (!site || !org) return <AppShell title="Accident report" back={`/sites/${siteId ?? ''}/forms`}><div className="banner banner-error">{error ?? 'Site not found.'}</div></AppShell>;

  // Completion screen — shown once the submit succeeds (online or
  // offline). Gives a clear "logged" moment with a short reference the
  // user can quote. The short ref is the first 8 chars of the UUID —
  // unique enough for in-conversation reference, full UUID is on the
  // detail page.
  if (done) {
    const dt = done.when.toLocaleString('en-GB', {
      weekday: 'short', day: '2-digit', month: 'short',
      hour: '2-digit', minute: '2-digit',
    });
    const shortRef = done.formId.slice(0, 8).toUpperCase();
    return (
      <AppShell title="Incident logged" back={`/sites/${site.id}/forms`}>
        <div
          style={{
            margin: '0 0 16px',
            padding: '20px 18px',
            background: 'rgba(22, 163, 74, 0.10)',
            border: '1px solid var(--success, #16A34A)',
            borderRadius: 12,
            textAlign: 'center',
          }}
        >
          <p style={{ fontSize: 32, margin: '0 0 6px', lineHeight: 1 }} aria-hidden>✅</p>
          <h2 style={{ margin: 0, fontSize: 20, color: 'var(--success, #16A34A)' }}>
            Incident logged
          </h2>
          {done.offline && (
            <p className="muted" style={{ margin: '6px 0 0', fontSize: 13 }}>
              Saved offline — will sync when you're back online.
            </p>
          )}
        </div>

        <div className="card" style={{ marginBottom: 16 }}>
          <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 14 }}>
            <tbody>
              <tr>
                <td style={{ padding: '6px 0', color: 'var(--ink-soft)', width: '40%' }}>Reference</td>
                <td style={{ padding: '6px 0', fontWeight: 600, fontFamily: 'monospace' }}>{shortRef}</td>
              </tr>
              <tr>
                <td style={{ padding: '6px 0', color: 'var(--ink-soft)' }}>Type</td>
                <td style={{ padding: '6px 0', fontWeight: 600 }}>{done.type}</td>
              </tr>
              <tr>
                <td style={{ padding: '6px 0', color: 'var(--ink-soft)' }}>Logged at</td>
                <td style={{ padding: '6px 0', fontWeight: 600 }}>{dt}</td>
              </tr>
              <tr>
                <td style={{ padding: '6px 0', color: 'var(--ink-soft)' }}>Site</td>
                <td style={{ padding: '6px 0', fontWeight: 600 }}>{done.siteName}</td>
              </tr>
            </tbody>
          </table>
        </div>

        <div className="btn-stack">
          <button
            type="button"
            className="btn btn-primary"
            onClick={() => nav(`/forms/${done.formId}`)}
          >
            View incident
          </button>
          <button
            type="button"
            className="btn btn-ghost"
            onClick={() => nav(`/sites/${site.id}/forms`)}
          >
            Done
          </button>
        </div>
      </AppShell>
    );
  }

  return (
    <AppShell title="Accident / near-miss" back={`/sites/${site.id}/forms`}>
      <div className="card" style={{ background: 'var(--danger-light)', borderColor: 'var(--danger)', marginBottom: 12 }}>
        <h3 style={{ margin: 0, color: 'var(--danger)' }}>Accident / near-miss</h3>
        <p style={{ margin: '4px 0 0' }}>Log every incident — however minor. Complete within 24 hours. Tell the Director immediately for serious injuries.</p>
      </div>

      {error && <div className="banner banner-error">{error}</div>}

      {restorable && !restoreBannerDismissed && (
        <div
          className="card"
          style={{
            background: '#FFF8E5',
            borderColor: 'var(--warn, #BF8E3D)',
            marginBottom: 12,
          }}
          role="status"
        >
          <p style={{ margin: '0 0 8px', fontWeight: 600 }}>
            Pick up where you left off?
          </p>
          <p className="muted" style={{ margin: '0 0 12px', fontSize: 13 }}>
            We saved a draft of this report {formatDraftAge(restorable.savedAt)}. Restore it or start fresh.
          </p>
          <div className="btn-stack" style={{ gap: 8, flexDirection: 'row' }}>
            <button type="button" className="btn btn-primary" onClick={restoreDraft}>
              Restore my draft
            </button>
            <button type="button" className="btn btn-ghost" onClick={discardDraft}>
              Start fresh
            </button>
          </div>
        </div>
      )}

      <form onSubmit={onSubmit}>
        {/* Photos are promoted to the very top of the form per the UX
            refresh — site managers usually shoot a photo first (often
            with gloves on and one hand free), and the photo itself
            anchors the rest of the entry. The DictateButton mics on
            every text field below cover the "voice note" half of the
            same brief. */}
        <h3 className="section-title" style={{ marginTop: 0, marginBottom: 8 }}>
          📷 Photos
        </h3>
        <p className="muted" style={{ margin: '0 0 10px', fontSize: 13 }}>
          Snap the scene first. Every text field below has a mic — tap it to
          dictate instead of type if your hands are full.
        </p>
        <PhotoAttach photos={photos} onChange={setPhotos} />

        <h3 className="section-title" style={{ marginTop: 22, marginBottom: 8 }}>
          The basics
        </h3>
        <div className="field">
          <label htmlFor="acc-type">Type *</label>
          <select id="acc-type" required value={type} onChange={(e) => setType(e.target.value)}>
            {TYPES.map((t) => <option key={t} value={t}>{t || 'Choose…'}</option>)}
          </select>
        </div>
        <div className="field-row">
          <div className="field"><label htmlFor="acc-date">Date *</label><input id="acc-date" type="date" required value={date} onChange={(e) => setDate(e.target.value)} /></div>
          <div className="field"><label htmlFor="acc-time">Time *</label><input id="acc-time" type="time" required value={time} onChange={(e) => setTime(e.target.value)} /></div>
        </div>
        <div className="field">
          <label htmlFor="acc-location">Location on site *</label>
          <div className="input-with-mic">
            <input id="acc-location" type="text" required placeholder="e.g. Plot 3 north scaffold, lift 2" value={location} onChange={(e) => setLocation(e.target.value)} />
            <DictateButton onTranscript={(t) => setLocation((p) => (p ? p + ' ' + t : t))} />
          </div>
        </div>
        <div className="field"><label htmlFor="acc-person">Person(s) involved</label><input id="acc-person" type="text" placeholder="e.g. Mike Doyle" value={person} onChange={(e) => setPerson(e.target.value)} /></div>
        <div className="field"><label htmlFor="acc-employer">Their employer</label><input id="acc-employer" type="text" value={employer} onChange={(e) => setEmployer(e.target.value)} /></div>
        <div className="field"><label htmlFor="acc-occupation">Their job / occupation</label><input id="acc-occupation" type="text" placeholder="e.g. Bricklayer" value={personOccupation} onChange={(e) => setPersonOccupation(e.target.value)} /></div>
        <div className="field">
          <label htmlFor="acc-address">Their home address</label>
          <p className="muted" style={{ margin: '4px 0', fontSize: 12 }}>Required for the accident book if someone was hurt.</p>
          <textarea id="acc-address" rows={2} value={personAddress} onChange={(e) => setPersonAddress(e.target.value)} />
        </div>
        <div className="field">
          <label htmlFor="acc-what">What happened *</label>
          <div className="input-with-mic">
            <textarea id="acc-what" rows={3} required value={what} onChange={(e) => setWhat(e.target.value)} />
            <DictateButton onTranscript={(t) => setWhat((p) => (p ? p + ' ' + t : t))} />
          </div>
        </div>
        <div className="field">
          <label htmlFor="acc-injury">Injury / damage</label>
          <div className="input-with-mic">
            <textarea id="acc-injury" rows={2} placeholder="e.g. 5cm cut to right shin, no broken skin to bone." value={injury} onChange={(e) => setInjury(e.target.value)} />
            <DictateButton onTranscript={(t) => setInjury((p) => (p ? p + ' ' + t : t))} />
          </div>
        </div>
        <div className="field"><label htmlFor="acc-body">Part of body affected</label><input id="acc-body" type="text" placeholder="e.g. Left wrist" value={bodyPart} onChange={(e) => setBodyPart(e.target.value)} /></div>
        <div className="field"><label htmlFor="acc-fa">First aid given by</label><input id="acc-fa" type="text" value={fa} onChange={(e) => setFa(e.target.value)} /></div>
        <div className="field">
          <label htmlFor="acc-treatment">Sent for further treatment?</label>
          <select id="acc-treatment" value={treatment} onChange={(e) => setTreatment(e.target.value)}>
            {TREATMENTS.map((t) => <option key={t}>{t}</option>)}
          </select>
        </div>
        {treatment !== 'No' && (
          <div className="field"><label htmlFor="acc-hospital">Hospital / clinic name</label><input id="acc-hospital" type="text" placeholder="e.g. Harrogate District Hospital" value={hospital} onChange={(e) => setHospital(e.target.value)} /></div>
        )}
        <div className="field-row">
          <div className="field">
            <label htmlFor="acc-timeoff">Time off work</label>
            <select id="acc-timeoff" value={timeOff} onChange={(e) => setTimeOff(e.target.value)}>
              {TIME_OFF.map((t) => <option key={t}>{t}</option>)}
            </select>
          </div>
          <div className="field"><label htmlFor="acc-return">Back at work on</label><input id="acc-return" type="date" value={returnDate} onChange={(e) => setReturnDate(e.target.value)} /></div>
        </div>
        {timeOff === 'Off work — more than 7 days' && (
          <div className="card" style={{ background: '#FAF1E0', borderColor: 'var(--warn)', marginBottom: 12 }}>
            <p style={{ margin: 0, fontSize: 13 }}>
              <strong>Over 7 days off is RIDDOR-reportable.</strong> Tick "Off normal work for 7+ days" below and report to HSE within 15 days.
            </p>
          </div>
        )}
        <div className="field">
          <label>Witnesses</label>
          <p className="muted" style={{ margin: '4px 0', fontSize: 12 }}>
            Each person who saw it. Name + a one-line account of what they saw. Adds weight to the report.
          </p>
          {witnessList.map((w, i) => (
            <div key={i} className="card" style={{ marginBottom: 8, position: 'relative', paddingTop: 8 }}>
              <button
                type="button"
                aria-label="Remove witness"
                onClick={() => setWitnessList((prev) => prev.filter((_, idx) => idx !== i))}
                style={{ position: 'absolute', top: 6, right: 6, background: 'transparent', border: 'none', color: 'var(--ink-soft)', fontSize: 18, cursor: 'pointer', lineHeight: 1 }}
              >×</button>
              <div className="field" style={{ marginBottom: 6 }}>
                <label htmlFor={`acc-witness-name-${i}`} style={{ fontSize: 11 }}>Witness name</label>
                <input
                  id={`acc-witness-name-${i}`}
                  type="text"
                  placeholder="e.g. Pete Hill"
                  value={w.name}
                  onChange={(e) => setWitnessList((prev) => prev.map((x, idx) => idx === i ? { ...x, name: e.target.value } : x))}
                />
              </div>
              <div className="field" style={{ marginBottom: 0 }}>
                <label htmlFor={`acc-witness-saw-${i}`} style={{ fontSize: 11 }}>What they saw</label>
                <textarea
                  id={`acc-witness-saw-${i}`}
                  rows={2}
                  placeholder="e.g. Saw Mike on the platform — brick slipped off as he shifted his stack."
                  value={w.statement}
                  onChange={(e) => setWitnessList((prev) => prev.map((x, idx) => idx === i ? { ...x, statement: e.target.value } : x))}
                />
              </div>
            </div>
          ))}
          <button
            type="button"
            className="btn btn-ghost"
            onClick={() => setWitnessList((prev) => [...prev, { name: '', statement: '' }])}
          >
            + Add witness
          </button>
        </div>
        <div className="field">
          <label htmlFor="acc-cause">What caused it?</label>
          <p className="muted" style={{ margin: '4px 0', fontSize: 12 }}>
            Plain English. e.g. "Trip on cable across walkway — cable was meant to be in trunking, fitter ran out of trunking and laid temp cable to crack on."
          </p>
          <div className="input-with-mic">
            <textarea id="acc-cause" rows={3} value={cause} onChange={(e) => setCause(e.target.value)} />
            <DictateButton onTranscript={(t) => setCause((p) => (p ? p + ' ' + t : t))} />
          </div>
        </div>
        <div className="field">
          <label htmlFor="acc-action">Action — to prevent recurrence *</label>
          <div className="input-with-mic">
            <textarea id="acc-action" rows={2} required placeholder="e.g. Toolbox talk on scaffold housekeeping. Replaced damaged board." value={actionTaken} onChange={(e) => setActionTaken(e.target.value)} />
            <DictateButton onTranscript={(t) => setActionTaken((p) => (p ? p + ' ' + t : t))} />
          </div>
        </div>
        <div className="field-row">
          <div className="field"><label htmlFor="acc-owner">Who's doing it</label><input id="acc-owner" type="text" placeholder="e.g. Site manager" value={actionOwner} onChange={(e) => setActionOwner(e.target.value)} /></div>
          <div className="field"><label htmlFor="acc-due">To be done by</label><input id="acc-due" type="date" value={actionDue} onChange={(e) => setActionDue(e.target.value)} /></div>
        </div>

        <h3 className="section-title" style={{ marginTop: 22 }}>Does this need reporting to HSE?</h3>
        <p className="muted" style={{ margin: '0 0 12px', fontSize: 13 }}>
          Quick triage first — we'll show the detail only if you need it.
        </p>
        <div
          role="radiogroup"
          aria-label="Was anyone seriously hurt?"
          style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr', gap: 8, marginBottom: 12 }}
        >
          {(['yes', 'unsure', 'no'] as const).map((opt) => {
            const labels = { yes: 'Yes', unsure: 'Not sure', no: 'No' } as const;
            const helps  = { yes: 'Someone is hurt', unsure: 'Walk me through it', no: 'Just logging it' } as const;
            const active = riddorTriage === opt;
            return (
              <button
                key={opt}
                type="button"
                role="radio"
                aria-checked={active}
                onClick={() => {
                  setRiddorTriage(opt);
                  // Switching to 'no' clears any boxes the user ticked
                  // before changing their mind — otherwise isRiddor would
                  // misclassify a No-triage as RIDDOR-reportable.
                  if (opt === 'no') setRiddor({});
                }}
                className="riddor-triage-btn"
                data-active={active}
              >
                <strong>{labels[opt]}</strong>
                <span className="muted" style={{ display: 'block', fontSize: 12, marginTop: 2 }}>
                  {helps[opt]}
                </span>
              </button>
            );
          })}
        </div>

        {/* Triage path: No — green confirmation, no further questions. */}
        {riddorTriage === 'no' && (
          <div
            className="card"
            style={{ background: '#E8F4EA', borderColor: 'var(--success, #16A34A)', marginTop: 4 }}
          >
            <p style={{ margin: 0, fontWeight: 600, color: 'var(--success-deep, #15803D)' }}>
              ✓ Not RIDDOR-reportable
            </p>
            <p className="muted" style={{ margin: '4px 0 0', fontSize: 13 }}>
              Log it on the records, share the lessons learned, and move on. No HSE submission needed.
            </p>
          </div>
        )}

        {/* Triage path: Yes / Not sure — show the 13 categories so the user
            can identify which (if any) applies. Same content as before. */}
        {showRiddorCategories && (
          <>
            <p className="muted" style={{ margin: '4px 0 8px', fontSize: 13 }}>
              Tick anything that fits. If nothing here matches but you're worried, phone HSE — better to ask.
            </p>
            <div className="check-list">
              {RIDDOR_ITEMS.map((r) => (
                <label key={r.key} className="check-row">
                  <input
                    type="checkbox"
                    checked={!!riddor[r.key]}
                    onChange={(e) => setRiddor((prev) => ({ ...prev, [r.key]: e.target.checked }))}
                  />
                  <span>
                    {r.label}
                    {r.hint && <span className="muted" style={{ display: 'block', fontSize: 12, marginTop: 2 }}>{r.hint}</span>}
                  </span>
                </label>
              ))}
            </div>
          </>
        )}

        {/* HSE-report warning: only shows when at least one of the 13 boxes
            is ticked — the triage Yes/Not-sure alone isn't enough to flag. */}
        {isRiddor && (
          <div className="card" style={{ background: '#FAF1E0', borderColor: 'var(--warn)', marginTop: 12 }}>
            <p style={{ margin: '0 0 6px' }}>
              <strong>You need to report this to HSE.</strong>
            </p>
            <p className="muted" style={{ margin: 0, fontSize: 13 }}>
              Report at <strong>hse.gov.uk/riddor</strong> within 10 days, or call <strong>0345 300 9923</strong>.
              Fatalities and major injuries — phone <strong>immediately</strong>.
            </p>
          </div>
        )}

        {/* Edge case: user said Yes/Not-sure but ticked nothing. Soft nudge —
            don't block, but make sure they know "no category ticked" doesn't
            automatically mean "not reportable". */}
        {showRiddorCategories && !isRiddor && (
          <div
            className="card"
            style={{ background: '#FFF8E5', borderColor: 'var(--warn)', marginTop: 12 }}
          >
            <p style={{ margin: 0, fontSize: 13 }}>
              <strong>Nothing ticked yet.</strong> If you genuinely can't tell whether it fits any of these,{' '}
              <strong>phone HSE on 0345 300 9923</strong> and ask. Better to check than not.
            </p>
          </div>
        )}

        {isRiddor && (
          <div className="card" style={{ marginTop: 12 }}>
            <h4 style={{ margin: '0 0 6px' }}>Once you've submitted, log the reference</h4>
            <p className="muted" style={{ margin: '0 0 12px', fontSize: 13 }}>
              You can submit the form now and come back to fill these in later, or fill them in here if you've already submitted.
            </p>
            <div className="field-row">
              <div className="field">
                <label htmlFor="r-ref">F2508 reference (RIDDOR form number)</label>
                <input
                  id="r-ref"
                  type="text"
                  placeholder="e.g. F2508-2026-12345"
                  value={riddorRef}
                  onChange={(e) => setRiddorRef(e.target.value)}
                />
              </div>
              <div className="field">
                <label htmlFor="r-date">Submitted on</label>
                <input
                  id="r-date"
                  type="date"
                  value={riddorSubmittedAt}
                  onChange={(e) => setRiddorSubmittedAt(e.target.value)}
                />
              </div>
            </div>
          </div>
        )}

        <h3 className="section-title" style={{ marginTop: 22 }}>Reported by</h3>
        <div className="field-row">
          <div className="field"><label htmlFor="acc-by-name">Name *</label><input id="acc-by-name" type="text" required placeholder="Your name" value={byName} onChange={(e) => setByName(e.target.value)} /></div>
          <div className="field"><label htmlFor="acc-by-role">Role</label><input id="acc-by-role" type="text" value={byRole} onChange={(e) => setByRole(e.target.value)} /></div>
        </div>

        <h3 className="section-title" style={{ marginTop: 22 }}>Signature *</h3>
        <SignaturePad ref={sigRef} label="Signed by reporter" />
        <p className="muted" style={{ margin: '8px 0 12px', fontSize: 12 }}>
          This record contains personal information. It's stored securely and only visible to people in your company.
        </p>

        <div className="btn-stack">
          <button type="submit" className="btn btn-primary" disabled={submitting}>
            {submitting ? 'Saving…' : 'Save report'}
          </button>
          <button type="button" className="btn btn-ghost" onClick={() => nav(`/sites/${site.id}/forms`)}>Cancel</button>
        </div>
      </form>
    </AppShell>
  );
}
